import { ContractPerson } from '@ekt-group/general-purpose-api-interfaces/src/entities/contract-person.entity';
import { CalendarCheck02 } from '@untitled-ui/icons-react';
import { DatePicker, Switch } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormInput from '@/components/hook-form-components/FormInput';
import { DATE_FORMAT } from '@/constants';
import { cn } from '@/utils/cn';

export type AttorneyFormValues = {
  personalCode: string;
  startDate: string;
  endDate: string;
  firstname: string;
  surname: string;
  furtherMandateRight: boolean;
};

interface AttorneyFormProps {
  contractPerson?: ContractPerson;
  onSubmit: (values: AttorneyFormValues) => void;
}

const AttorneyForm = ({ contractPerson, onSubmit }: AttorneyFormProps) => {
  const { t } = useTranslation('contractAttorneyModal');

  const defaultValues = {
    personalCode: contractPerson?.contractOwner?.personalCode || '',
    firstname: contractPerson?.contractOwner?.firstname || '',
    surname: contractPerson?.contractOwner?.surname || '',
    startDate: contractPerson?.startDate || null,
    endDate: contractPerson?.endDate || null,
    furtherMandateRight: contractPerson?.furtherMandateRight || false,
  };

  const form = useForm<AttorneyFormValues>({
    defaultValues,
  });

  useEffect(() => {
    if (contractPerson) {
      form.reset(defaultValues);
    }
  }, [contractPerson, form]);

  return (
    <FormProvider {...form}>
      <form id="attorney-form" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 sm:text-base text-sm">
        <div className="flex flex-col gap-2">
          <span className="font-medium text-gray-10">{t('personalCode')}</span>
          <FormInput name="personalCode" control={form.control} placeholder={t('personalCode')} />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-medium text-gray-10">{t('startDate')}</span>
          <Controller
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <DatePicker
                format={DATE_FORMAT}
                prefix={<CalendarCheck02 className="text-brand-brown w-6 h-6" />}
                suffixIcon={null}
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => field.onChange(date)}
                className={cn(
                  'w-full border bg-gray-100 md:h-12 h-10 flex justify-between items-center px-4 gap-2 cursor-pointer rounded-lg',
                  field.value ? 'border-gray-300 bg-white' : 'border-gray-100',
                )}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-medium text-gray-10">{t('endDate')}</span>
          <Controller
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <DatePicker
                format={DATE_FORMAT}
                prefix={<CalendarCheck02 className="text-brand-brown w-6 h-6" />}
                suffixIcon={null}
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => field.onChange(date)}
                className={cn(
                  'w-full border bg-gray-100 md:h-12 h-10 flex justify-between items-center px-4 gap-2 cursor-pointer rounded-lg',
                  field.value ? 'border-gray-300 bg-white' : 'border-gray-100',
                )}
              />
            )}
          />
        </div>

        <div className="flex sm:gap-3 gap-5 sm:flex-row flex-col">
          <div className="flex flex-col gap-2 sm:flex-1 flex-none">
            <span className="font-medium text-gray-10">
              {t('firstname')} ({t('optional', { ns: 'common' })})
            </span>
            <FormInput name="firstname" control={form.control} placeholder={t('firstname')} />
          </div>

          <div className="flex flex-col gap-2 sm:flex-1 flex-none">
            <span className="font-medium text-gray-10">
              {t('surname')} ({t('optional', { ns: 'common' })})
            </span>
            <FormInput name="surname" control={form.control} placeholder={t('surname')} />
          </div>
        </div>

        <div className="flex gap-2 flex-1 h-12 items-center">
          <Controller
            name="furtherMandateRight"
            control={form.control}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                className="aria-checked-[checked=true]:bg-[#D5DC2E] outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 ring-0"
              />
            )}
          />
          <span>{t('furtherMandateRight')}</span>
        </div>
      </form>
    </FormProvider>
  );
};

export default AttorneyForm;
